import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

interface StorageEstimateWithDetails extends StorageEstimate {
  readonly usageDetails?: Readonly<Record<string, number>>;
}

async function collect(): Promise<RawData> {
  if (!navigator.storage) {
    return { available: false };
  }
  const estimate =
    (await navigator.storage.estimate()) as StorageEstimateWithDetails;
  const usageDetails = Object.fromEntries(
    Object.entries(estimate.usageDetails ?? {}).filter(
      ([, value]) => typeof value == 'number',
    ),
  );
  return {
    usage: estimate.usage ?? null,
    quota: estimate.quota ?? null,
    usageDetails,
  };
}

function formatBytes(value: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let amount = value;
  let unitIndex = 0;
  while (amount >= 1024 && unitIndex < units.length - 1) {
    amount /= 1024;
    unitIndex += 1;
  }
  const rounded =
    unitIndex == 0 ? Math.round(amount) : Number(amount.toFixed(1));
  return `${String(rounded)} ${units[unitIndex]}`;
}

function validateStorageEstimate(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (rawData.available == false) {
    return validation(['Storage estimate is unavailable']);
  }
  const usage = rawData.usage;
  const quota = rawData.quota;
  if (typeof usage != 'number' || !Number.isFinite(usage) || usage < 0) {
    reasons.push('Storage usage must be a nonnegative finite number');
  }
  if (typeof quota != 'number' || !Number.isFinite(quota) || quota < 0) {
    reasons.push('Storage quota must be a nonnegative finite number');
  }
  if (typeof usage == 'number' && typeof quota == 'number' && quota < usage) {
    reasons.push('Storage quota must not be less than usage');
  }
  if (!isRecord(rawData.usageDetails)) {
    reasons.push('Storage usage details must be an object');
  } else {
    for (const [name, value] of Object.entries(rawData.usageDetails)) {
      if (typeof value != 'number' || !Number.isFinite(value) || value < 0) {
        reasons.push(`${name} usage must be a nonnegative finite number`);
      }
    }
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  if (rawData.available == false) {
    return 'Unavailable';
  }
  const usage = rawData.usage;
  const quota = rawData.quota;
  if (typeof usage != 'number' || typeof quota != 'number') {
    return 'Invalid estimate';
  }
  const free = Math.max(0, quota - usage);
  const value = `${formatBytes(usage)} used · ${formatBytes(free)} free`;
  if ([...value].length <= 32) {
    return value;
  }
  return `${formatBytes(usage)} / ${formatBytes(free)} free`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateStorageEstimate(rawData);
}

export const storageEstimateTest: TestDefinition = {
  id: 'storageEstimate',
  title: 'Storage estimate',
  category: 'variableProfile',
  format,
  run,
  validate,
};
